import { useTranslation } from "react-i18next";
import { Link, useNavigate, useParams } from "react-router";
import { FoodsIcon, FoodsTabs, FoodsTabTemplateOption } from "@foodpilot/foods";
import { Stack, Typography, useTheme } from "@mui/material";
import { ExtensionPoint, EXTENSION_POINTS } from "src/components/PuckExtensions";

import { useCompanyMetadata } from "src/context/Company/Metadata/useCompanyMetadata";
import { URLHandler } from "src/services/URLHandler";
import { getSsqDisplayTitle } from "src/utils/ssqTitle";
import { OneSsqOverview } from "./OneScoreOverview";
import { Overview } from "./Overview";
import { useCompanyOverviewContext } from "src/context/Company/Overview/useCompanyOverviewContext";
import { useEffect } from "react";

export const MyCompany = () => {
    const { company } = useCompanyMetadata();
    const { ssqId } = useParams();
    const navigate = useNavigate();

    const { overview } = useCompanyOverviewContext();

    const { t } = useTranslation();

    const theme = useTheme();

    const tabs: FoodsTabTemplateOption[] = [];
    if (overview?.ssqs.length > 1) {
        const url = URLHandler.company.overview(company.id);
        tabs.push({
            id: undefined,
            name: (
                <Link
                    to={url}
                    style={{
                        textDecoration: "none",
                        color: "inherit",
                    }}
                >
                    <Stack flexDirection="row" alignItems={"center"}>
                        <FoodsIcon icon={"dashboard2"} size={2} />
                        <Typography variant="small-bold">{t("company.dashboard.overview", "Overview")}</Typography>
                    </Stack>
                </Link>
            ),
            TabProps: {
                onClick: () => {
                    navigate(url);
                },
            },
        });
    }

    const tabsWithQuestionnaire = overview?.ssqs.reduce((accTabs, ssq) => {
        const _firstScore = ssq.scores.find((i) => true);
        const score = ssq.scores.length > 1 ? undefined : _firstScore;
        const url = URLHandler.company.overviewSsq(company.id, ssq.id, score?.id);
        accTabs.push({
            id: ssq.id,
            name: (
                <Link
                    to={url}
                    style={{
                        textDecoration: "none",
                        color: "inherit",
                    }}
                >
                    <Typography variant="small-bold">{`${getSsqDisplayTitle(ssq)}`}</Typography>
                </Link>
            ),
            TabProps: {
                onClick: () => {
                    navigate(url);
                },
            },
        });
        return accTabs;
    }, tabs);

    const selectedTab = tabsWithQuestionnaire.findIndex((i) => {
        const idToFind = ssqId === undefined ? undefined : Number(ssqId);
        return i.id === idToFind;
    });

    // Auto-redirect to first ssq when there's only one ssq and no ssqId in URL
    useEffect(() => {
        if (overview && ssqId === undefined && tabsWithQuestionnaire.length === 1) {
            const firstSsq = overview.ssqs[0];
            const url = URLHandler.company.overviewSsq(company.id, firstSsq.id);
            navigate(url, { replace: true });
        }
    }, [overview, ssqId, tabsWithQuestionnaire, company.id, navigate]);

    return (
        <Stack gap={{ xs: theme.spacing(2), lg: theme.spacing(3) }}>
            <ExtensionPoint extensionPoint={EXTENSION_POINTS.COMPANY_SCORES_HEADER} />
            <FoodsTabs
                forceScrollable
                tabs={tabsWithQuestionnaire}
                selectedTabIndex={selectedTab ?? 0}
                tabVariant={"buttons"}
                textVariant={"small-bold"}
            />
            <ExtensionPoint extensionPoint={EXTENSION_POINTS.COMPANY_SCORES_SIDEBAR} />
            {ssqId === undefined ?
                <Overview overview={overview} />
            :   <OneSsqOverview />}
            <ExtensionPoint extensionPoint={EXTENSION_POINTS.COMPANY_SCORES_FOOTER} />
        </Stack>
    );
};
